// Export templates (F-13.1) and canvas sizing (D-29): cells are on the comparison grid (A × compareScale).

import type { SourceId } from '../core/types';

export type ExportKind = 'internal' | 'client' | 'still';
export type InternalTemplate = 'sbs' | 'sbs-zoom' | 'quad' | 'diff';
export type ClientTemplate = 'sbs' | 'sbs-zoom' | 'wipe' | 'quad';

export const MAX_W = 3840;
export const MAX_H = 2160;

export interface CellSpec {
  x: number;
  y: number;
  w: number;
  h: number;
  kind: 'full' | 'zoom' | 'diff' | 'wipe';
  src: SourceId;
  /** Second source for diff/wipe cells. */
  b?: SourceId;
}

export interface TemplateLayout {
  width: number;
  height: number;
  cells: CellSpec[];
  /** Output scale relative to the comparison grid (1 = native, < 1 = scaled down, D-29). */
  scale: number;
}

function even8(v: number): number {
  return Math.max(8, Math.round(v / 8) * 8);
}

/**
 * Lays out a template. gridW/gridH is the comparison grid (A × k). The B list is the versions to show;
 * sbs/sbs-zoom/diff/wipe use the first B.
 */
export function layoutTemplate(tpl: InternalTemplate | ClientTemplate, gridW: number, gridH: number, bs: SourceId[], half: boolean, base: SourceId = 'A'): TemplateLayout {
  const b = bs[0] ?? 'B1';
  let cols = 2;
  let rows = 1;
  if (tpl === 'sbs-zoom' || tpl === 'quad') rows = 2;
  if (tpl === 'wipe') cols = 1;
  const nativeW = gridW * cols;
  const nativeH = gridH * rows;
  let scale = Math.min(1, MAX_W / nativeW, MAX_H / nativeH);
  if (half) scale *= 0.5;
  const cw = even8(gridW * scale);
  const ch = even8(gridH * scale) - (even8(gridH * scale) % 2);
  const cells: CellSpec[] = [];
  const at = (c: number, r: number) => ({ x: c * cw, y: r * ch, w: cw, h: ch });
  if (tpl === 'sbs') cells.push({ ...at(0, 0), kind: 'full', src: 'A' }, { ...at(1, 0), kind: 'full', src: b });
  // Difference and wipe compare against the chosen reference (A, or another version, F-05.9).
  else if (tpl === 'diff') cells.push({ ...at(0, 0), kind: 'full', src: base }, { ...at(1, 0), kind: 'diff', src: base, b });
  else if (tpl === 'sbs-zoom')
    cells.push(
      { ...at(0, 0), kind: 'full', src: 'A' },
      { ...at(1, 0), kind: 'full', src: b },
      { ...at(0, 1), kind: 'zoom', src: 'A' },
      { ...at(1, 1), kind: 'zoom', src: b },
    );
  else if (tpl === 'quad') {
    const ids: SourceId[] = ['A', 'B1', 'B2', 'B3'];
    ids.forEach((id, i) => cells.push({ ...at(i % 2, Math.floor(i / 2)), kind: 'full', src: id }));
  } else if (tpl === 'wipe') cells.push({ ...at(0, 0), kind: 'wipe', src: base, b });
  return { width: cw * cols, height: ch * rows, cells, scale: cw / gridW };
}
